import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, DatePicker, Row, Select, Typography, Space, Button, Tag, Tooltip, Empty, Spin, Table, Tour } from "antd";
import {
    FileTextOutlined, CalendarOutlined, FilterOutlined, EyeOutlined,
    FilePdfOutlined, FileExcelOutlined, BankOutlined, ApartmentOutlined,
    AlertOutlined, DollarOutlined, AuditOutlined, PercentageOutlined, ContainerOutlined,
    SearchOutlined, QuestionCircleOutlined, TableOutlined, MedicineBoxOutlined, ToolOutlined,
    FieldTimeOutlined, GlobalOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { frenchDateOnly } from "../../../variables/functions";
import PreviewModal from "./components/PreviewModal";
import jsPDF from "jspdf";
import { drawHeaderSinistrePdfGenerator, generateSinistresExcel, sinistresPdfGenerator } from "../../sinistres/hooks/generatesHook";
import {
    generateBonAPayerExcel, generateBonAPayerPDF, generateConseilSyntheseExcel,
    generateConseilSynthesePDF, generateContentieuxExcel, generateContentieuxPDF,
    generateDecaissementsExcel,
    generateDecaissementsHonorairesPDF, generateDecaissementsPDF, generateRatioSP,
    generateSinistresDeclaresExcel, generateSinistresDeclaresPDF,
    generateSinistresTardifsExcel, generateSinistresTardifsPDF,
    generateSinistresSurvenusExcel, generateSinistresSurvenusPDF,
    buildCadenceModel, generateCadencePDF, generateCadenceExcel,
    generateDisciplinePDF, generateDisciplineExcel, setEtatCompagnie, getEtatCompagnie, setEtatSinistreFiltre,
} from "./etatsSinistresFunctions";
import CadenceTable from "./components/CadenceTable";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const dateFormat = "YYYY-MM-DD";
const PRIMARY = "#242166";
const TOUT_BRANCHE = { codeBranche: "TOUT", nom: "TOUT" };

// Libellés lisibles des codes StatutDossier (cf. backend mapEtat).
const ETAT_LABELS = {
    A: "À valider", I: "En instance", O: "Ouvert", T: "Terminé",
    S: "Sans suite", R: "Réouvert", Ta: "Tardif", F: "Forclose", Rf: "Refusé",
};
// Libellés du FILTRE « État du sinistre » (valeurs du Select ; 4 = « Tous » → aucun libellé affiché),
// pour l'injecter dans les en-têtes d'export via setEtatSinistreFiltre.
const ETAT_FILTRE_LABELS = {
    1: "En cours", 2: "Terminé", 3: "Sans suite", 5: "Réouverts", 6: "Tardifs",
};

const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
// Compagnie de l'utilisateur non-Pool (auto-sélectionnée, non modifiable).
const USER_ORG = LOGGED_USER?.profil?.organisme;
const USER_COMPAGNIE = USER_ORG?.code ? { code: USER_ORG.code, raisonSociale: USER_ORG.raisonSociale || USER_ORG.code } : null;

const EtatsSinistresV2 = () => {
    const [branches, setBranches] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [agences, setAgences] = useState([]);
    const [conseils, setConseils] = useState([]);
    const [selectedConseil, setSelectedConseil] = useState(null); // { conseilJuridiqueId, nom }
    const [juridictions, setJuridictions] = useState([]); // paramètres type JURIDICTION
    const [selectedJuridiction, setSelectedJuridiction] = useState(null);

    const [reportKey, setReportKey] = useState("declares");
    const [date, setDate] = useState(null);
    const [selectedBranche, setSelectedBranche] = useState(TOUT_BRANCHE);
    // Pool : libre. Non-Pool : verrouillé sur sa propre compagnie.
    const [selectedCompagnie, setSelectedCompagnie] = useState(isPool ? null : USER_COMPAGNIE);
    const [selectedAgence, setSelectedAgence] = useState(null);
    // 4 = « Tous les sinistres » (le backend traite etat==4 comme aucun filtre d'état).
    const [selectedEtat, setSelectedEtat] = useState(4);

    const [loading, setLoading] = useState(false);
    const [lastCount, setLastCount] = useState(null);
    const [datas, setDatas] = useState([]); // résultats affichés dans le tableau
    const [preview, setPreview] = useState(null); // { url, title, downloadName, excelFunction }
    const [openTour, setOpenTour] = useState(() => localStorage.getItem("etatsV2TourSeen") !== "1");
    const closeTour = () => { setOpenTour(false); localStorage.setItem("etatsV2TourSeen", "1"); };

    // Cibles du tour guidé
    const refReport = useRef(null);
    const refFilters = useRef(null);
    const refSearch = useRef(null);
    const refDownload = useRef(null);

    useEffect(() => {
        axiosInstance.get(`/api/branche`).then((res) => setBranches(res.data || [])).catch((e) => console.log(e));
        // Inclut le Pool en plus des compagnies → états des sinistres du Pool.
        axiosInstance.get(`/api/organisme/all/assurances-pool`).then((res) => setCompagnies(res.data || [])).catch((e) => console.log(e));
        axiosInstance.get(`/api/agence/dtrf`).then((res) => setAgences(res.data || [])).catch((e) => console.log(e));
        axiosInstance.get(`/api/conseil-juridique`)
            .then((res) => setConseils(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => console.log(e));
        axiosInstance.get(`/api/parametre/type/JURIDICTION`)
            .then((res) => setJuridictions(res.data || [])).catch((e) => console.log(e));
    }, []);

    const brancheCode = selectedBranche?.codeBranche || null;
    const isGUM = brancheCode === "GUM";

    // ── Configuration des 6 rapports ────────────────────────────────────
    // branche/etat/compagnie : indique les filtres que l'endpoint exploite.
    const REPORTS = useMemo(() => ([
        {
            key: "declares", label: "Sinistres déclarés", icon: <AuditOutlined />, color: "#1890ff",
            endpoint: "/api/sinistre/all", branche: true, etat: true, compagnie: true,
            pdf: (d) => generateSinistresDeclaresPDF(d, brancheCode, date),
            excel: (d) => generateSinistresDeclaresExcel(d, date),
        },
        {
            key: "survenus", label: "Sinistres survenus", icon: <AlertOutlined />, color: "#fa8c16",
            endpoint: "/api/sinistre/all/survenance", branche: true, etat: true, compagnie: true,
            pdf: (d) => generateSinistresSurvenusPDF(d, brancheCode, date),
            excel: (d) => generateSinistresSurvenusExcel(d, date),
        },
        {
            key: "tardifs", label: "Sinistres tardifs (déclarés année N+1)", icon: <FieldTimeOutlined />, color: "#c41d7f",
            endpoint: "/api/sinistre/all", branche: true, etat: true, compagnie: true,
            pdf: (d) => generateSinistresTardifsPDF(d, brancheCode, date),
            excel: (d) => generateSinistresTardifsExcel(d, date),
        },
        {
            key: "carte-brune", label: "Dossiers Carte Brune (transfrontaliers)", icon: <GlobalOutlined />, color: "#ad6800",
            endpoint: "/api/sinistre/carte-brune", branche: false, etat: false, compagnie: true,
            pdf: (d) => generateSinistresDeclaresPDF(d, brancheCode, date),
            excel: (d) => generateSinistresDeclaresExcel(d, date),
        },
        {
            key: "cadence", label: "Cadence de règlement", icon: <TableOutlined />, color: "#242166",
            endpoint: "/api/sinistre/cadence-reglements", branche: true, etat: true, compagnie: true,
            pdf: (d) => generateCadencePDF(buildCadenceModel(d), date),
            excel: (d) => generateCadenceExcel(buildCadenceModel(d), date),
        },
        {
            key: "payes", label: "Sinistres payés (décaissements)", icon: <DollarOutlined />, color: "#52c41a",
            endpoint: "/api/decaissement/all-payes", branche: true, etat: false, compagnie: true,
            pdf: (d) => generateDecaissementsPDF(d, brancheCode, date),
            excel: (d) => generateDecaissementsExcel(d, brancheCode, date),
        },
        {
            key: "honoraires", label: "Honoraires payés (retenues)", icon: <ContainerOutlined />, color: "#722ed1",
            endpoint: "/api/decaissement/all-honoraires-payes", branche: true, etat: false, compagnie: true,
            pdf: (d) => generateDecaissementsHonorairesPDF(d, brancheCode, date),
            excel: null, // pas d'export Excel dédié
        },
        {
            key: "reglements-medical", label: "Règlements médicaux (médecins/pharmacies)", icon: <MedicineBoxOutlined />, color: "#08979c",
            endpoint: "/api/decaissement/reglements-discipline", discipline: "MEDICALE",
            branche: true, etat: true, compagnie: true,
            pdf: (d) => generateDisciplinePDF(d, "MÉDICAUX", brancheCode, date),
            excel: (d) => generateDisciplineExcel(d, "Medicaux", date),
        },
        {
            key: "reglements-meca", label: "Règlements mécaniques (garages/experts)", icon: <ToolOutlined />, color: "#d48806",
            endpoint: "/api/decaissement/reglements-discipline", discipline: "MECANIQUE",
            branche: true, etat: true, compagnie: true,
            pdf: (d) => generateDisciplinePDF(d, "MÉCANIQUES", brancheCode, date),
            excel: (d) => generateDisciplineExcel(d, "Mecaniques", date),
        },
        {
            key: "bons", label: "Sinistres bons à payer", icon: <FileTextOutlined />, color: "#13c2c2",
            endpoint: "/api/decaissement/sinistres-bons-a-payer", branche: true, etat: false, compagnie: true,
            pdf: (d) => generateBonAPayerPDF(d, brancheCode, date),
            excel: (d) => generateBonAPayerExcel(d, brancheCode, date),
        },
        {
            key: "ratio", label: "Ratio S/P", icon: <PercentageOutlined />, color: "#eb2f96",
            endpoint: "/api/stats/ratio-sp", branche: false, etat: false, compagnie: true,
            pdf: (d) => generateRatioSP(d, date),
            excel: null,
        },
        {
            key: "conseil-synthese", label: "Synthèse conseil juridique", icon: <BankOutlined />, color: "#7c3aed",
            endpoint: "/api/decaissement/conseils-juridiques/synthese",
            branche: true, etat: false, compagnie: false, conseil: true,
            pdf: (d) => generateConseilSynthesePDF(d, selectedConseil?.nom || "", date),
            excel: (d) => generateConseilSyntheseExcel(d, selectedConseil?.nom || "", date),
        },
        {
            key: "contentieux", label: "Sinistres en contentieux", icon: <AuditOutlined />, color: "#b45309",
            endpoint: "/api/sinistre/contentieux/etat",
            branche: true, etat: false, compagnie: false, conseil: true, juridiction: true,
            pdf: (d) => generateContentieuxPDF(d, selectedConseil?.nom || "", date),
            excel: (d) => generateContentieuxExcel(d, selectedConseil?.nom || "", date),
        },
        {
            // Ancien bouton « Exporter » du listing des sinistres, déplacé ici comme rapport.
            key: "cima", label: "Etats CIMA Tableau vivants", icon: <FileTextOutlined />, color: "#0e7490",
            endpoint: "/api/sinistre/etat-cima-v2", branche: true, etat: false, compagnie: true,
            pdf: (d) => {
                const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
                const pageWidth = doc.internal.pageSize.getWidth();
                const formattedDateTime = new Date().toLocaleString("fr-FR", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                });
                drawHeaderSinistrePdfGenerator(doc, pageWidth, getEtatCompagnie());
                return sinistresPdfGenerator(d, doc, {}, formattedDateTime);
            },
            excel: (d) => generateSinistresExcel(d, getEtatCompagnie()),
        },
    ]), [brancheCode, date, selectedConseil]);

    const report = REPORTS.find((r) => r.key === reportKey) || REPORTS[0];

    const buildDownloadName = () => {
        const periode = date ? `du ${frenchDateOnly(date[0])} au ${frenchDateOnly(date[1])}` : "";
        const stamp = new Date().toLocaleString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
        return `${report.label} ${periode} _ ${stamp}.pdf`;
    };

    const fetchReportData = async () => {
        const params = {
            startDate: date ? date[0] : null,
            endDate: date ? date[1] : null,
        };
        if (report.branche && brancheCode && brancheCode !== "TOUT") params.branche = brancheCode;
        if (report.etat) params.etat = selectedEtat;
        // compagnie : uniquement sur les rapports sinistres (l'endpoint l'accepte).
        // On passe une CHAÎNE simple (pas un tableau) : axios sérialiserait un tableau en
        // "compagnie[]=03", or Tomcat rejette les caractères [ ] (RFC 7230/3986). Spring
        // lie bien "compagnie=03" à un List<String>.
        // On n'envoie le filtre compagnie que pour le Pool. Pour les non-Pool, le backend
        // force déjà le cloisonnement sur leur propre compagnie (côté service/contexte rôle).
        if (report.compagnie && isPool && selectedCompagnie?.code) params.compagnie = `${selectedCompagnie.code}`.trim();
        // Synthèse conseil / contentieux : filtre optionnel par conseil (cabinet) + juridiction.
        if (report.conseil && selectedConseil?.conseilJuridiqueId) params.conseil = selectedConseil.conseilJuridiqueId;
        if (report.juridiction && selectedJuridiction) params.juridiction = selectedJuridiction;
        // Règlements médicaux/mécaniques : discipline figée par le rapport.
        if (report.discipline) params.discipline = report.discipline;
        const { data } = await axiosInstance.get(report.endpoint, { params });
        return data;
    };

    const handleAction = async (action) => {
        if (report.branche && brancheCode == null) {
            toast.warning("Veuillez sélectionner une branche.");
            return;
        }
        setLoading(true);
        // Contexte « organisme/compagnie » injecté dans l'en-tête PDF + Excel de tous les états qui
        // portent une sélection de compagnie (rapports Pool-only → chaîne vide, aucune ligne ajoutée).
        const compagnieLabel = report.compagnie
            ? (selectedCompagnie?.raisonSociale
                || (isPool ? "Toutes les compagnies" : USER_COMPAGNIE?.raisonSociale) || "")
            : "";
        setEtatCompagnie(compagnieLabel);
        // Contexte « État du sinistre » (filtre choisi) injecté au sous-titre PDF + tête Excel, au même
        // titre que catégorie/organisme. Seulement pour les rapports qui exploitent ce filtre et hors
        // « Tous les sinistres » (4 = aucun filtre d'état).
        setEtatSinistreFiltre((report.etat && selectedEtat !== 4) ? (ETAT_FILTRE_LABELS[selectedEtat] || "") : "");
        try {
            const data = await fetchReportData();
            const raw = Array.isArray(data) ? data : (data ? [data] : []);
            // Sinistres PAYÉS = uniquement ceux AVEC un numéro de chèque ;
            // sinistres BONS À PAYER = uniquement ceux SANS numéro de chèque.
            const aCheque = (r) => r?.numeroCheque != null && `${r.numeroCheque}`.trim() !== "";
            // Sinistre TARDIF = déclaré une année civile après (ou plus) l'année de survenance.
            const estTardif = (r) => {
                if (!r?.dateSinistre || !r?.dateDeclaration) return false;
                return new Date(r.dateDeclaration).getFullYear() > new Date(r.dateSinistre).getFullYear();
            };
            const filtree = report.key === "payes" ? raw.filter(aCheque)
                : report.key === "bons" ? raw.filter((r) => !aCheque(r))
                    : report.key === "tardifs" ? raw.filter(estTardif)
                        : raw;
            // Clé de ligne UNIQUE par index (évite les clés dupliquées/nulles qui bloquent
            // la pagination AntD ; la fonction rowKey ne reçoit pas l'index en AntD v5).
            const rows = filtree.map((r, i) => ({ ...r, __rowKey: i }));
            setDatas(rows);
            // Cadence : le compte pertinent = nb de sinistres déclarés (pas le nb de cellules).
            if (report.key === "cadence") {
                const nbDeclares = rows.filter((r) => r.typeLigne === "DECLARE")
                    .reduce((s, r) => s + (Number(r.nbSinistres) || 0), 0);
                setLastCount(nbDeclares);
            } else {
                setLastCount(rows.length);
            }

            // « Rechercher » : on charge simplement le tableau (sans PDF ni téléchargement).
            if (action === "search") {
                if (!rows.length) toast.info("Aucun enregistrement pour ces critères.");
                return;
            }

            if (action === "excel") {
                if (report.excel) report.excel(filtree);
                else toast.info("Aucun export Excel disponible pour ce rapport.");
                return;
            }

            const url = await report.pdf(filtree); // certains générateurs sont async, d'autres sync
            if (!url) {
                toast.info("Aucune donnée à afficher pour ces critères.");
                return;
            }
            if (action === "pdf") {
                const a = document.createElement("a");
                a.href = url; a.download = buildDownloadName();
                document.body.appendChild(a); a.click(); a.remove();
            } else {
                setPreview({
                    url,
                    title: report.label,
                    downloadName: buildDownloadName(),
                    excelFunction: report.excel ? () => report.excel(filtree) : null,
                });
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de la génération du rapport.");
        } finally {
            setLoading(false);
        }
    };

    // ── Styles ──────────────────────────────────────────────────────────
    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };
    const fieldLabel = { display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

    // Compagnie affichée pour tous les rapports ; modifiable seulement par le Pool.
    const showCompagnie = !!report.compagnie;

    // ── Colonnes du tableau de résultats (selon le type de rapport) ─────
    const fmtNum = (v) => (v != null && v !== "" ? Number(v).toLocaleString("fr-FR") : "-");
    const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "-");

    // N° sinistre : on affiche le M (numeroSinistreM) ; à défaut le P (numeroSinistreP / num_sinistre).
    const sinistreNum = (r) =>
        r.numeroSinistreM || r.numeroSinistreP || r.numSinistre || r.numeroSinistrem || r.numeroSinistrep || "-";

    // Champs jamais affichés dans les colonnes auto (états de règlements).
    const HIDDEN_KEYS = ["codeId", "code_id"];
    const SINISTRE_NUM_KEYS = ["numeroSinistreM", "numeroSinistreP", "numSinistre", "numeroSinistrem", "numeroSinistrep"];

    const sinistreColumns = (survenance) => [
        { title: "N° sinistre", key: "num", render: (_, r) => sinistreNum(r) },
        { title: survenance ? "Survenance" : "Déclaration", key: "date", render: (_, r) => fmtDate(survenance ? r.dateSinistre : r.dateDeclaration) },
        { title: "Police", key: "police", render: (_, r) => r.numeroPoliceP || r.numeroPoliceM || "-" },
        { title: "Assuré", key: "assure", render: (_, r) => `${r.nom || ""} ${r.prenom || ""}`.trim() || "-" },
        { title: "Catégorie", dataIndex: "categorieCode", key: "cat", render: (v) => v || "-" },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immat", render: (v) => v || "-" },
        { title: "Évaluation", dataIndex: "evaluation", key: "eval", align: "right", render: fmtNum },
        { title: "État", dataIndex: "etat", key: "etat", render: (v) => ETAT_LABELS[v] || v || "-" },
        { title: "Organisme", dataIndex: "organisme", key: "org", render: (v) => v || "-" },
    ];

    // Sinistres tardifs : survenance + déclaration + retard (années), sur la base « déclarés ».
    const anneesRetard = (r) => {
        if (!r?.dateSinistre || !r?.dateDeclaration) return null;
        return new Date(r.dateDeclaration).getFullYear() - new Date(r.dateSinistre).getFullYear();
    };
    const tardifColumns = () => [
        { title: "N° sinistre", key: "num", render: (_, r) => sinistreNum(r) },
        { title: "Survenance", key: "surv", render: (_, r) => fmtDate(r.dateSinistre) },
        { title: "Déclaration", key: "decl", render: (_, r) => fmtDate(r.dateDeclaration) },
        {
            title: "Retard", key: "retard", align: "center",
            render: (_, r) => { const y = anneesRetard(r); return y != null ? <Tag color="magenta">+{y} an{y > 1 ? "s" : ""}</Tag> : "-"; },
        },
        { title: "Police", key: "police", render: (_, r) => r.numeroPoliceP || r.numeroPoliceM || "-" },
        { title: "Assuré", key: "assure", render: (_, r) => `${r.nom || ""} ${r.prenom || ""}`.trim() || "-" },
        { title: "Catégorie", dataIndex: "categorieCode", key: "cat", render: (v) => v || "-" },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immat", render: (v) => v || "-" },
        { title: "Évaluation", dataIndex: "evaluation", key: "eval", align: "right", render: fmtNum },
        { title: "État", dataIndex: "etat", key: "etat", render: (v) => ETAT_LABELS[v] || v || "-" },
        { title: "Organisme", dataIndex: "organisme", key: "org", render: (v) => v || "-" },
    ];

    // Générique : colonnes dérivées des champs primitifs de la 1re ligne (pour les autres rapports).
    // On masque le codeId et on fusionne le numéro de sinistre (M sinon P) en une seule colonne.
    const genericColumns = (rows) => {
        if (!rows?.length) return [];
        const first = rows[0];
        const cols = [];

        if (SINISTRE_NUM_KEYS.some((k) => k in first)) {
            cols.push({ title: "N° sinistre", key: "numSinistre", render: (_, r) => sinistreNum(r) });
        }

        Object.keys(first)
            .filter((k) => !HIDDEN_KEYS.includes(k) && !SINISTRE_NUM_KEYS.includes(k))
            .filter((k) => {
                const v = first[k];
                return v == null || ["string", "number", "boolean"].includes(typeof v);
            })
            .slice(0, 12)
            .forEach((k) => cols.push({
                title: k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()),
                dataIndex: k, key: k,
                // Champs « date » (dateSinistre, dateReglement…) → format français ; nombres → fr-FR.
                render: (v) => {
                    if (v == null || v === "") return "-";
                    if (/date/i.test(k)) return fmtDate(v);
                    return typeof v === "number" ? v.toLocaleString("fr-FR") : v;
                },
            }));

        return cols;
    };

    // Police unique = M sinon P (au lieu de 2 colonnes vides).
    const policeOf = (r) => r.numeroPoliceM || r.numeroPoliceP || "-";

    // Colonnes explicites des décaissements (payés / bons à payer) : on retire les colonnes vides
    // (souscripteur, retenue, totalCount) ; chèque/banque uniquement pour les payés ; motif pour les bons.
    const decaissementColumns = (isBons) => [
        { title: "N° Sinistre", key: "numSinistre", render: (_, r) => sinistreNum(r) },
        { title: "Date Sinistre", key: "dateSinistre", render: (_, r) => fmtDate(r.dateSinistre) },
        // Bons à payer → date de l'ordre de dépense (dateReglement). Payés → DATE DU CHÈQUE
        // (dateEmissionCheque, posée à l'assignation), avec repli sur dateReglement.
        { title: isBons ? "Date Ordre de dépense" : "Date Règlement", key: "dateReglement", render: (_, r) => fmtDate(isBons ? r.dateReglement : (r.dateEmissionCheque || r.dateReglement)) },
        { title: isBons ? "Montant à Payer" : "Montant Payé", dataIndex: "montantPaye", key: "montantPaye", align: "right", render: fmtNum },
        ...(!isBons ? [
            { title: "N° Chèque", dataIndex: "numeroCheque", key: "numeroCheque", render: (v) => v || "-" },
            { title: "Banque", dataIndex: "banque", key: "banque", render: (v) => v || "-" },
        ] : []),
        { title: "Bénéficiaire", dataIndex: "beneficiaire", key: "beneficiaire", render: (v) => v || "-" },
        ...(isBons ? [
            { title: "Motif Règlement", dataIndex: "motifReglement", key: "motifReglement", render: (v) => v || "-" },
        ] : []),
        { title: "Police", key: "police", render: (_, r) => policeOf(r) },
    ];

    // Honoraires payés (retenues) : champs réellement renseignés (bénéficiaire, motif, montant, retenue).
    const honorairesColumns = () => [
        { title: "N° Sinistre", key: "numSinistre", render: (_, r) => sinistreNum(r) },
        { title: "Bénéficiaire", dataIndex: "beneficiaire", key: "beneficiaire", render: (v) => v || "-" },
        { title: "Date Règlement", key: "dateReglement", render: (_, r) => fmtDate(r.dateReglement) },
        { title: "Motif Règlement", dataIndex: "motifReglement", key: "motifReglement", render: (v) => v || "-" },
        { title: "Montant", dataIndex: "montantPaye", key: "montantPaye", align: "right", render: fmtNum },
        { title: "Retenue", dataIndex: "retenue", key: "retenue", align: "right", render: fmtNum },
    ];

    // Règlements médicaux / mécaniques : liste plate (experts + établissements).
    const disciplineColumns = () => [
        { title: "N° Sinistre", dataIndex: "numeroSinistre", key: "numeroSinistre", render: (v) => v || "-" },
        { title: "Bénéficiaire", dataIndex: "beneficiaire", key: "beneficiaire", render: (v) => v || "-" },
        {
            title: "Type", key: "type", render: (_, r) => (
                <Space size={4}>
                    <Tag color={r.source === "EXPERT" ? "geekblue" : "green"} style={{ marginInlineEnd: 0 }}>
                        {r.source === "EXPERT" ? "Expert" : "Établissement"}
                    </Tag>
                    <span>{r.typeLibelle || "-"}</span>
                </Space>
            ),
        },
        { title: "Date sinistre", key: "dateSinistre", render: (_, r) => fmtDate(r.dateSinistre) },
        { title: "Date règlement", key: "dateReglement", render: (_, r) => fmtDate(r.dateReglement) },
        { title: "Montant", dataIndex: "montant", key: "montant", align: "right", render: fmtNum },
        { title: "N° Chèque", dataIndex: "numeroCheque", key: "numeroCheque", render: (v) => v || "-" },
        { title: "Motif", dataIndex: "motif", key: "motif", render: (v) => v || "-" },
        { title: "Police", dataIndex: "numeroPolice", key: "numeroPolice", render: (v) => v || "-" },
        { title: "Catégorie", dataIndex: "categorieCode", key: "categorieCode", render: (v) => v || "-" },
        { title: "Organisme", dataIndex: "organisme", key: "organisme", render: (v) => v || "-" },
    ];

    // Ratio S/P par BRANCHE (1 ligne TPV, 1 ligne VT).
    const ratioColumns = () => [
        { title: "Branche", key: "branche", render: (_, r) => r.branche || r.categorie || "-" },
        { title: "Nombre contrats", dataIndex: "nombreContrats", key: "nombreContrats", align: "right", render: fmtNum },
        { title: "Primes totales", dataIndex: "primesTotales", key: "primesTotales", align: "right", render: fmtNum },
        { title: "Nombre sinistres", dataIndex: "nombreSinistres", key: "nombreSinistres", align: "right", render: fmtNum },
        { title: "Montant sinistres", dataIndex: "montantSinistres", key: "montantSinistres", align: "right", render: fmtNum },
        { title: "Ratio S/P (%)", dataIndex: "ratioSP", key: "ratioSP", align: "right", render: (v) => (v != null ? `${Number(v).toLocaleString("fr-FR")} %` : "-") },
    ];

    // Synthèse conseil juridique : 1 ligne/dossier, postes en colonnes (Année sans dataIndex → non sommée).
    const syntheseConseilColumns = () => [
        { title: "N° Sinistre", dataIndex: "numeroSinistre", key: "numeroSinistre", render: (v) => v || "-" },
        { title: "Juridiction", dataIndex: "juridiction", key: "juridiction", render: (v) => v || "-" },
        { title: "Date Sinistre", key: "dateSinistre", render: (_, r) => fmtDate(r.dateSinistre) },
        { title: "Année", key: "annee", render: (_, r) => r.anneeSurvenance || "-" },
        { title: "Police", dataIndex: "numeroPolice", key: "numeroPolice", render: (v) => v || "-" },
        { title: "Assuré", dataIndex: "assure", key: "assure", render: (v) => v || "-" },
        { title: "Gestion", dataIndex: "fraisGestion", key: "fraisGestion", align: "right", render: fmtNum },
        { title: "PV constat", dataIndex: "fraisPvConstat", key: "fraisPvConstat", align: "right", render: fmtNum },
        { title: "Vignette", dataIndex: "fraisVignette", key: "fraisVignette", align: "right", render: fmtNum },
        { title: "Déplacement", dataIndex: "fraisDeplacement", key: "fraisDeplacement", align: "right", render: fmtNum },
        { title: "Honoraires", dataIndex: "honoraires", key: "honoraires", align: "right", render: fmtNum },
        { title: "TVA", dataIndex: "tva", key: "tva", align: "right", render: fmtNum },
        { title: "Retenue", dataIndex: "retenue", key: "retenue", align: "right", render: fmtNum },
        { title: "Net à payer", dataIndex: "montantNet", key: "montantNet", align: "right", render: fmtNum },
    ];

    // Sinistres en contentieux (situation des dossiers affectés à un cabinet).
    const contentieuxColumns = () => [
        { title: "N° Sinistre", dataIndex: "numeroSinistre", key: "numeroSinistre", render: (v) => v || "-" },
        { title: "Juridiction", dataIndex: "juridiction", key: "juridiction", render: (v) => v || "-" },
        { title: "Année", key: "annee", render: (_, r) => r.anneeSurvenance || "-" },
        { title: "Date Sinistre", key: "dateSinistre", render: (_, r) => fmtDate(r.dateSinistre) },
        { title: "Assuré", dataIndex: "assure", key: "assure", render: (v) => v || "-" },
        { title: "Évaluation", dataIndex: "evaluation", key: "evaluation", align: "right", render: fmtNum },
        { title: "Montant total payé", dataIndex: "montantTotalPaye", key: "montantTotalPaye", align: "right", render: fmtNum },
        { title: "SAP", dataIndex: "sap", key: "sap", align: "right", render: fmtNum },
        { title: "Résumé du dossier", dataIndex: "resumeDossier", key: "resumeDossier", render: (v) => v || "-" },
    ];

    // Dossiers Carte Brune : liste plate + SAP + cumul règlements (chèque au BNCB).
    const carteBruneColumns = () => [
        { title: "N° sinistre", key: "num", render: (_, r) => sinistreNum(r) },
        { title: "Survenance", key: "surv", render: (_, r) => fmtDate(r.dateSinistre) },
        { title: "Police", key: "police", render: (_, r) => policeOf(r) },
        { title: "Assuré", key: "assure", render: (_, r) => `${r.nom || ""} ${r.prenom || ""}`.trim() || "-" },
        { title: "Catégorie", dataIndex: "categorieCode", key: "cat", render: (v) => v || "-" },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immat", render: (v) => v || "-" },
        { title: "Évaluation", dataIndex: "evaluation", key: "eval", align: "right", render: fmtNum },
        { title: "Cumul des paiements", dataIndex: "cumulReglements", key: "cumul", align: "right", render: fmtNum },
        { title: "SAP", dataIndex: "sap", key: "sap", align: "right", render: fmtNum },
        { title: "Compagnie", dataIndex: "organisme", key: "org", render: (v) => v || "-" },
    ];

    const tableColumns = report.key === "declares" ? sinistreColumns(false)
        : report.key === "carte-brune" ? carteBruneColumns()
        : report.key === "tardifs" ? tardifColumns()
        : report.key === "survenus" ? sinistreColumns(true)
            : report.key === "payes" ? decaissementColumns(false)
                : report.key === "bons" ? decaissementColumns(true)
                    : report.key === "honoraires" ? honorairesColumns()
                        : (report.key === "reglements-medical" || report.key === "reglements-meca") ? disciplineColumns()
                            : report.key === "ratio" ? ratioColumns()
                                : report.key === "conseil-synthese" ? syntheseConseilColumns()
                                    : report.key === "contentieux" ? contentieuxColumns()
                                        : genericColumns(datas);

    // ── Tour guidé (zoom étape par étape) ───────────────────────────────
    const tourSteps = [
        {
            title: "1. Type de rapport",
            description: "Commencez par choisir le type d'état à générer (sinistres déclarés, survenus, payés, honoraires, bons à payer, ratio S/P).",
            target: () => refReport.current,
            nextButtonProps: { children: "Suivant" },
        },
        {
            title: "2. Composer les filtres",
            description: `Affinez ensuite avec la période, la branche, l'état du sinistre${isPool ? ", la compagnie" : ""}… (combinez plusieurs filtres).`,
            target: () => refFilters.current,
            nextButtonProps: { children: "Suivant" },
            prevButtonProps: { children: "Précédent" },
        },
        {
            title: "3. Rechercher",
            description: "Cliquez sur « Rechercher » pour afficher les enregistrements correspondants dans le tableau.",
            target: () => refSearch.current,
            nextButtonProps: { children: "Suivant" },
            prevButtonProps: { children: "Précédent" },
        },
        {
            title: "4. Télécharger",
            description: "Enfin, téléchargez au format voulu : « Aperçu » (prévisualisation PDF), « PDF », ou « Excel ».",
            target: () => refDownload.current,
            nextButtonProps: { children: "Terminer" },
            prevButtonProps: { children: "Précédent" },
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête sobre */}
                            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                                <div>
                                    <Title level={3} style={{ margin: 0, color: PRIMARY }}>
                                        <FileTextOutlined style={{ marginRight: 10 }} /> États des sinistres
                                    </Title>
                                    <Text type="secondary">Sélectionnez un rapport, affinez les filtres, puis prévisualisez ou téléchargez (PDF / Excel).</Text>
                                </div>
                                <Button icon={<QuestionCircleOutlined />} onClick={() => setOpenTour(true)}>Tutoriel</Button>
                            </div>

                            {/* Sélecteur de rapport (chips) */}
                            <Card ref={refReport} style={{ ...cardStyle, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                                <div style={fieldLabel}><ContainerOutlined /> Type de rapport</div>
                                <Row gutter={[12, 12]}>
                                    {REPORTS.map((r) => {
                                        const active = r.key === reportKey;
                                        return (
                                            <Col xs={24} sm={12} lg={8} key={r.key}>
                                                <div
                                                    onClick={() => { setReportKey(r.key); setDatas([]); setLastCount(null); }}
                                                    style={{
                                                        cursor: "pointer", borderRadius: 12, padding: "12px 14px",
                                                        border: `1.5px solid ${active ? r.color : "#eef0f4"}`,
                                                        background: active ? `${r.color}10` : "#fff",
                                                        display: "flex", alignItems: "center", gap: 12, transition: "all .2s",
                                                    }}
                                                >
                                                    <span style={{
                                                        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                                                        background: `${r.color}1a`, color: r.color, fontSize: 18,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}>{r.icon}</span>
                                                    <span style={{ fontWeight: 600, color: active ? PRIMARY : "#374151", fontSize: 13.5 }}>{r.label}</span>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card>

                            {/* Filtres */}
                            <Card ref={refFilters} style={{ ...cardStyle, marginBottom: 16 }} bodyStyle={{ padding: 18 }}>
                                <Row gutter={[16, 16]} align="bottom">
                                    <Col xs={24} md={8}>
                                        <div style={fieldLabel}><CalendarOutlined /> Période</div>
                                        <RangePicker
                                            format={dateFormat}
                                            onChange={(_, ds) => setDate(ds && ds[0] ? ds : null)}
                                            placeholder={["Date début", "Date fin"]}
                                            style={{ width: "100%", height: 40, borderRadius: 8 }}
                                        />
                                    </Col>

                                    {report.branche && (
                                        <Col xs={24} sm={12} md={5}>
                                            <div style={fieldLabel}><FilterOutlined /> Branche</div>
                                            <Select
                                                placeholder="Toutes les branches" style={{ width: "100%" }}
                                                value={brancheCode || undefined}
                                                onChange={(_, opt) => setSelectedBranche(opt?.data || null)}
                                                options={[
                                                    { value: TOUT_BRANCHE.codeBranche, label: TOUT_BRANCHE.nom, data: TOUT_BRANCHE },
                                                    ...branches.map((b) => ({ value: b.codeBranche, label: b.nom, data: b })),
                                                ]}
                                            />
                                        </Col>
                                    )}

                                    {showCompagnie && (
                                        <Col xs={24} sm={12} md={6}>
                                            <div style={fieldLabel}><BankOutlined /> Compagnie</div>
                                            <Select
                                                showSearch optionFilterProp="label" allowClear={isPool}
                                                disabled={!isPool}
                                                placeholder={isPool ? "Toutes les compagnies" : undefined}
                                                style={{ width: "100%" }}
                                                value={selectedCompagnie?.code || undefined}
                                                onChange={(_, opt) => setSelectedCompagnie(opt?.data || null)}
                                                options={(isPool ? compagnies : (USER_COMPAGNIE ? [USER_COMPAGNIE] : []))
                                                    .map((c) => ({ value: c.code, label: c.raisonSociale, data: c }))}
                                            />
                                        </Col>
                                    )}

                                    {report.conseil && (
                                        <Col xs={24} sm={12} md={6}>
                                            <div style={fieldLabel}><BankOutlined /> Conseil juridique</div>
                                            <Select
                                                showSearch optionFilterProp="label" allowClear
                                                placeholder="Tous les conseils"
                                                style={{ width: "100%" }}
                                                value={selectedConseil?.conseilJuridiqueId || undefined}
                                                onChange={(_, opt) => setSelectedConseil(opt?.data || null)}
                                                options={conseils.map((c) => ({ value: c.conseilJuridiqueId, label: c.nom, data: c }))}
                                            />
                                        </Col>
                                    )}

                                    {report.juridiction && (
                                        <Col xs={24} sm={12} md={5}>
                                            <div style={fieldLabel}><FilterOutlined /> Juridiction</div>
                                            <Select
                                                showSearch optionFilterProp="label" allowClear
                                                placeholder="Toutes les juridictions"
                                                style={{ width: "100%" }}
                                                value={selectedJuridiction || undefined}
                                                onChange={(v) => setSelectedJuridiction(v || null)}
                                                options={juridictions.map((j) => ({ value: j.libelleParametre, label: j.libelleParametre }))}
                                            />
                                        </Col>
                                    )}

                                    {report.etat && (
                                        <Col xs={24} sm={12} md={5}>
                                            <div style={fieldLabel}><FilterOutlined /> État du sinistre</div>
                                            <Select
                                                style={{ width: "100%" }} value={selectedEtat} onChange={setSelectedEtat}
                                                options={[
                                                    { value: 4, label: "Tous les sinistres" },
                                                    { value: 1, label: "En cours" },
                                                    { value: 2, label: "Terminé" },
                                                    { value: 3, label: "Sans suite" },
                                                    { value: 5, label: "Réouverts" },
                                                    { value: 6, label: "Tardifs" },
                                                ]}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Card>

                            {/* Actions + résultat */}
                            <Card style={cardStyle} bodyStyle={{ padding: 18 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                    <Space size={10} wrap>
                                        <Tag color={report.color} style={{ padding: "4px 10px", borderRadius: 8, fontWeight: 600 }}>{report.label}</Tag>
                                        {date && <Text type="secondary">{frenchDateOnly(date[0])} → {frenchDateOnly(date[1])}</Text>}
                                        {lastCount != null && <Tag color="default">{lastCount} enregistrement(s)</Tag>}
                                    </Space>

                                    <Space size={10} wrap>
                                        <Button ref={refSearch} type="primary" icon={<SearchOutlined />} loading={loading}
                                            onClick={() => handleAction("search")}
                                            style={{ background: PRIMARY, height: 42, borderRadius: 8, fontWeight: 600 }}>
                                            Rechercher
                                        </Button>
                                        <Button ref={refDownload} icon={<EyeOutlined />} loading={loading}
                                            onClick={() => handleAction("preview")}
                                            style={{ height: 42, borderRadius: 8, fontWeight: 600, color: PRIMARY, borderColor: PRIMARY }}>
                                            Aperçu
                                        </Button>
                                        <Button icon={<FilePdfOutlined />} loading={loading}
                                            onClick={() => handleAction("pdf")}
                                            style={{ height: 42, borderRadius: 8, fontWeight: 600, color: "#1890ff", borderColor: "#1890ff" }}>
                                            PDF
                                        </Button>
                                        <Tooltip title={report.excel ? "" : "Export Excel non disponible pour ce rapport"}>
                                            <Button icon={<FileExcelOutlined />} loading={loading} disabled={!report.excel}
                                                onClick={() => handleAction("excel")}
                                                style={report.excel ? { height: 42, borderRadius: 8, fontWeight: 600, color: "#217346", borderColor: "#217346" } : { height: 42, borderRadius: 8 }}>
                                                Excel
                                            </Button>
                                        </Tooltip>
                                    </Space>
                                </div>

                                <div style={{ marginTop: 16 }}>
                                    {loading ? (
                                        <div style={{ textAlign: "center", padding: 40 }}><Spin tip="Chargement du rapport…" /></div>
                                    ) : report.key === "cadence" ? (
                                        datas.length > 0 ? (
                                            <CadenceTable model={buildCadenceModel(datas)} />
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={<Text type="secondary">Cliquez sur « Rechercher » pour afficher la cadence de règlement, puis « Aperçu » / PDF / Excel.</Text>}
                                            />
                                        )
                                    ) : datas.length > 0 ? (
                                        <Table
                                            size="small"
                                            rowKey={(r) => r.__rowKey ?? r.codeId ?? r.code_id}
                                            columns={tableColumns}
                                            dataSource={datas}
                                            scroll={{ x: "max-content" }}
                                            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "15", "30", "50", "100"], showTotal: (t) => `${t} enregistrement(s)` }}
                                            summary={["payes", "bons", "honoraires", "declares", "survenus", "tardifs", "conseil-synthese", "contentieux", "reglements-medical", "reglements-meca"].includes(report.key) ? () => (
                                                <Table.Summary fixed>
                                                    <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 600 }}>
                                                        {tableColumns.map((col, idx) => {
                                                            if (idx === 0) {
                                                                return <Table.Summary.Cell index={0} key="__total">{`TOTAL (${datas.length})`}</Table.Summary.Cell>;
                                                            }
                                                            const k = col.dataIndex;
                                                            const isNum = k && datas.some((r) => typeof r[k] === "number");
                                                            if (!isNum) {
                                                                return <Table.Summary.Cell index={idx} key={col.key ?? idx} />;
                                                            }
                                                            const sum = datas.reduce((acc, r) => acc + (typeof r[k] === "number" ? r[k] : 0), 0);
                                                            return (
                                                                <Table.Summary.Cell index={idx} key={col.key ?? idx}>
                                                                    {sum.toLocaleString("fr-FR")}
                                                                </Table.Summary.Cell>
                                                            );
                                                        })}
                                                    </Table.Summary.Row>
                                                </Table.Summary>
                                            ) : undefined}
                                        />
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={<Text type="secondary">Cliquez sur « Rechercher » pour afficher les enregistrements, puis « Aperçu » / PDF / Excel.</Text>}
                                        />
                                    )}
                                </div>
                            </Card>

                            <PreviewModal
                                visible={!!preview}
                                onClose={() => setPreview(null)}
                                url={preview?.url}
                                title={preview ? `Prévisualisation — ${preview.title}` : ""}
                                downloadName={preview?.downloadName}
                                excelFunction={preview?.excelFunction}
                            />

                            <Tour open={openTour} onClose={closeTour} steps={tourSteps} />

                            <Footer />
                            <div className="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default EtatsSinistresV2;
